import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { AgencyModal } from "../modals/AgencyModal";
import { EntityModal } from "../modals/EntityModal";
import { PolicyPrefixModal } from "../modals/PolicyPrefixModal";
import { ProducerModal } from "../modals/ProducerCodeModal";
import { useAgencyFromAgencyMapping } from "../hooks/useAgencyFromAgencyMapping";
import { useEntity } from "../hooks/useEntity";
import { IAgreementRepository } from "../types";

interface AgmtDetailsProps {
  agmtDetails: IAgreementRepository;
}
export const Truncate = ({ text, length }) => {
  if (text && text.length > length) {
    return (
      <span>
        {text.substring(0, length)}
        <span>...</span>
      </span>
    );
  }
  return <span>{text}</span>;
};

export const AgmtDetails: React.FC<AgmtDetailsProps> = ({ agmtDetails }) => {
  const userPermissions = useRecoilValue(permissionsSelector);
  const comValStatusPermssion = PERMISSIONS.permission_agmtField_comValStatus;
  const [firstCol, setFirstCol] = useState<any>([]);
  const [secondCol, setSecondCol] = useState<any>([]);
  const [entityShow, setEntityShow] = useState<true | false>(false);
  const [producerCodeShow, setProducerCodeShow] = useState<true | false>(false);
  const [policyPrefixShow, setPolicyPrefixShow] = useState<true | false>(false);
  const [agencyShow, setAgencyShow] = useState<true | false>(false);
  const [entityDetails, setEntityDetails] = useState([]);
  const [agencyDetails, setAgencyDetails] = useState([]);
  const { data: entities, isSuccess: useEntitySuccess } = useEntity();
  const producerCodeTruncated = Truncate({
    text: agmtDetails.producerCode,
    length: 24,
  });
  const policyPrefixTruncated = Truncate({
    text: agmtDetails.policyPrefix,
    length: 24,
  });
  const { data: agenciesFromMapping, isSuccess: useAgencySuccess } =
    useAgencyFromAgencyMapping();
  const newBusinessLiabilityRate = agmtDetails.newBusinessLiabilityRate ?? 0;
  const newBusinessPhysicalDamageRate =
    agmtDetails.newBusinessPhysicalDamageRate ?? 0;
  const renewalLiabilityRate = agmtDetails.renewalLiabilityRate ?? 0;
  const renewalPhysicalDamageRate = agmtDetails.renewalPhysicalDamageRate ?? 0;

  const slidingScales = agmtDetails.slidingScale
    ? agmtDetails.slidingScale
    : [];

  let dateToday = new Date();
  let expiredDate = agmtDetails.expirationDate
    ? new Date(
        new Date(agmtDetails.expirationDate).toLocaleDateString("en-US", {
          timeZone: "UTC",
        })
      )
    : "";

  useEffect(() => {
    //Handles Legal Entity Data for Modal
    if (agmtDetails.state) {
      const agmtStates = agmtDetails.state.split(",");

      if (agmtDetails && entities && useEntitySuccess) {
        if (agmtDetails.entityName) {
          if (agmtDetails.state === "ALL") {
            const selectedEntities = entities?.filter(
              (x) =>
                x.entityName?.toLowerCase() ===
                agmtDetails.entityName?.toLowerCase()
            );
            setEntityDetails(
              selectedEntities.sort((a, b) =>
                a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase() ? 1 : -1
              )
            );
          } else {
            const selectedEntities = entities?.filter(
              (x) =>
                x.entityName?.toLowerCase() ===
                  agmtDetails.entityName?.toLowerCase() &&
                agmtStates.includes(x.stateCode)
            );
            setEntityDetails(
              selectedEntities.sort((a, b) =>
                a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase() ? 1 : -1
              )
            );
          }
        } else {
          setEntityDetails([]);
        }
      }
      if (agmtDetails && agenciesFromMapping && useAgencySuccess) {
        if (agmtStates.includes("ALL")) {
          if (agmtDetails.agency === "ALL") {
            setAgencyDetails(
              agenciesFromMapping
                .filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase()
                    ? 1
                    : -1
                )
            );
          } else {
            setAgencyDetails(
              agenciesFromMapping
                ?.filter(
                  (x) =>
                    x.agencyName?.toLowerCase() ===
                    agmtDetails.agency?.toLowerCase()
                )
                .filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase()
                    ? 1
                    : -1
                )
            );
          }
        } else {
          if (agmtDetails.agency === "ALL") {
            setAgencyDetails(
              agenciesFromMapping
                ?.filter((x) => agmtStates.includes(x.stateCode))
                .filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase()
                    ? 1
                    : -1
                )
            );
          } else {
            setAgencyDetails(
              agenciesFromMapping
                ?.filter(
                  (x) =>
                    x.agencyName?.toLowerCase() ===
                      agmtDetails.agency?.toLowerCase() &&
                    agmtStates.includes(x.stateCode)
                )
                .filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase()
                    ? 1
                    : -1
                )
            );
          }
        }
      }
      //Producer Code Modal Details
      if (agmtDetails && entities && useEntitySuccess) {
        if (agmtDetails.entityName) {
          if (agmtDetails.state === "ALL") {
            const selectedEntities = entities?.filter(
              (x) =>
                x.entityName?.toLowerCase() ===
                agmtDetails.entityName?.toLowerCase()
            );
            setEntityDetails(
              selectedEntities.sort((a, b) =>
                a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase() ? 1 : -1
              )
            );
          } else {
            const selectedEntities = entities?.filter(
              (x) =>
                x.entityName?.toLowerCase() ===
                  agmtDetails.entityName?.toLowerCase() &&
                agmtStates.includes(x.stateCode)
            );
            setEntityDetails(
              selectedEntities.sort((a, b) =>
                a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase() ? 1 : -1
              )
            );
          }
        } else {
          setEntityDetails([]);
        }
      }
    } else {
      setAgencyDetails([]);
      setEntityDetails([]);
    }
  }, [
    agenciesFromMapping,
    agmtDetails,
    entities,
    useAgencySuccess,
    useEntitySuccess,
  ]);

  //truncate
  interface TruncateProps {
    text: string | undefined;
    length: number;
  }

  useEffect(() => {
    setFirstCol({
      carrier: ["Carrier", agmtDetails.carrier],
      state: ["State", agmtDetails?.state?.split(",").join(", ")],
      agency: [
        "Agency",
        <button
          onClick={() => setAgencyShow(true)}
          className="text-left underline text-quinary"
        >
          {agmtDetails.agency}
        </button>,
      ],
      legalEntity: [
        "Legal Entity",
        <button
          onClick={() => setEntityShow(true)}
          className="text-left underline text-quinary"
        >
          {agmtDetails.entityName}
        </button>,
      ],
      masterEntityName: ["MasterLegal Entity", agmtDetails.masterEntityName],
      effectiveDate: [
        "Effective Date",
        agmtDetails.effectiveDate
          ? new Date(agmtDetails.effectiveDate).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          : "",
      ],
      expirationDate: [
        "Expiration Date",
        agmtDetails.expirationDate
          ? new Date(agmtDetails.expirationDate).toLocaleDateString("en-US", {
              timeZone: "UTC",
            })
          : "",
      ],
      agrmtValStatus: [
        "Agreement Status",
        agmtDetails.agreementIsActive ? "Active" : "Inactive",
      ],
      CommVal: ["Used in CommVal", agmtDetails.isActive ? "True" : "False"],
      paidType: ["Commission Paid As", agmtDetails.paidType],
      billing: ["Billing", agmtDetails.billing],
      policyType: ["Policy Type", agmtDetails.policyType],
      prodCode: [
        "Producer Code",
        <button
          onClick={() => setProducerCodeShow(true)}
          className="text-left underline text-quinary"
        >
          {producerCodeTruncated}
        </button>,
      ],
      commissionPaymentDate: [
        "Day Commission Stmt Available",
        agmtDetails.commissionEffectiveDate
          ? new Date(agmtDetails.commissionEffectiveDate).toLocaleDateString(
              "en-US",
              {
                timeZone: "UTC",
              }
            )
          : agmtDetails.commissionEffectiveDate,
      ],
      commissionStatementDate: [
        "Frequency of Commission Payments",
        agmtDetails.commissionPaymentsFrequency,
      ],
      paymentRemittanceType: [
        "Payment Remittance Type",
        agmtDetails.paymentRemittanceType,
      ],
      commissionRemittance: [
        "Commission Remittance",
        agmtDetails.commissionRemittance,
      ],
      isWritingNewBusiness: [
        "Currently Writing New Business",
        agmtDetails.isWritingNewBusiness
          ? agmtDetails.expirationDate
            ? agmtDetails.expirationDate > new Date().toISOString().slice(0, 10)
              ? "Yes"
              : "No"
            : "Yes"
          : "No",
      ],
    });
    setSecondCol({
      lineOfBusinessName: ["Line of Business", agmtDetails.lineOfBusinessName],
      businessTypeName: ["Business Type", agmtDetails.businessTypeName],
      licenseTypeName: ["License Type", agmtDetails.licenseTypeName],
      agreementType: ["Agreement Type", agmtDetails.agreementType],
      program: ["Program", agmtDetails.program],
      policyPrefix: [
        "Policy Prefix",
        <button
          onClick={() => setPolicyPrefixShow(true)}
          className="text-left underline text-quinary"
        >
          {policyPrefixTruncated}
        </button>,
      ],
      planCode: ["Appointed By", agmtDetails.planCode],
      riskTypeName: ["Risk Type", agmtDetails.riskTypeName],
      standard: ["Standard", agmtDetails.standard],
      notes: ["Notes", agmtDetails.notes],
      salesLead: ["Sales Lead", agmtDetails.salesLeadName],
      commissionUpdatedby: [
        "Commision Updated By",
        agmtDetails.commissionUpdatedBy,
      ],
      commisionUpdatedOn: [
        "Commision Updated On",
        agmtDetails.commissionUpdatedOn
          ? new Date(agmtDetails.commissionUpdatedOn).toLocaleDateString(
              "en-US",
              {
                timeZone: "UTC",
              }
            )
          : agmtDetails.commissionUpdatedOn,
      ],

      contingentCommission: [
        "Contingent Commission",
        agmtDetails.contingentCommission ? "Yes" : "No",
      ],
    });
  }, [agmtDetails]);

  return (
    <>
      <div className="text-xs p-2 px-7">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div>
            {Object.values(firstCol).map((item: any, i) => {
              return item[0] === "Used in CommVal" &&
                !userPermissions.includes(comValStatusPermssion) ? (
                ""
              ) : (
                <div key={i} className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-left ml-2">
                      <span className="">{item[0]}: </span>
                    </label>
                    <span className="expandedData fontExpand ">{item[1]}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="">
            {Object.values(secondCol).map((item: any, i) => {
              return (
                <div key={i} className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-left ml-2">
                      <span className="">{item[0]}: </span>
                    </label>
                    <span className="expandedData fontExpand ">{item[1]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="commission-details py-4">
          <div className="text-base disclosureLabel">Commission Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
              <label className="expandedLabel fontExpand text-left ml-2">
                <span className="">Flat Rate Per Policy: </span>
              </label>
              <span className="expandedData fontExpand ">
                {agmtDetails.flatRatePerPolicy === true ? "True" : "False"}
              </span>
            </div>
          </div>
          {agmtDetails.flatRatePerPolicy && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                <label className="expandedLabel fontExpand text-left ml-2">
                  <span className="">New Flat Rate: </span>
                </label>

                <span className="expandedData fontExpand ">
                  {agmtDetails.newBusinessFlatRate > 0
                    ? "$" + agmtDetails.newBusinessFlatRate.toFixed(2)
                    : agmtDetails.newBusinessFlatRate}
                </span>
              </div>
              <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                <label className="expandedLabel fontExpand text-left ml-2">
                  <span className="">Renewal Flat Rate: </span>
                </label>

                <span className="expandedData fontExpand ">
                  {agmtDetails.renewalFlatRate > 0
                    ? "$" + agmtDetails.renewalFlatRate.toFixed(2)
                    : agmtDetails.renewalFlatRate}
                </span>
              </div>
            </div>
          )}
          {!agmtDetails.flatRatePerPolicy && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="">
                <div className="grid grid-cols-2 py-1">
                  <label className="expandedLabel fontExpand text-left ml-2">
                    <span className="">New Percentage: </span>
                  </label>
                  <span className="expandedData fontExpand ">
                    {agmtDetails.newPercentage}%
                  </span>
                </div>
                <div className="">
                  <div className="grid grid-cols-2 py-1">
                    <label className="expandedLabel fontExpand text-left ml-6">
                      <span className="">Confie %: </span>
                    </label>
                    <span className="expandedData fontExpand ">
                      {agmtDetails.newPercentageConfie}%
                    </span>
                  </div>
                  {agmtDetails.isFranchise && (
                    <div className="grid grid-cols-2 py-1">
                      <label className="expandedLabel fontExpand text-left ml-6">
                        <span className="">Franchise %: </span>
                      </label>
                      <span className="expandedData fontExpand ">
                        {agmtDetails.newPercentageFranchise}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="">
                <div className="grid grid-cols-2 py-1">
                  <label className="expandedLabel fontExpand text-left ml-2">
                    <span className="">Renewal %: </span>
                  </label>
                  <span className="expandedData fontExpand ">
                    {agmtDetails.renewalPercentage}%
                  </span>
                </div>
                <div className="">
                  <div className="grid grid-cols-2 py-1">
                    <label className="expandedLabel fontExpand text-left ml-6">
                      <span className="">Confie %: </span>
                    </label>
                    <span className="expandedData fontExpand ">
                      {agmtDetails.renewPercentageConfie}%
                    </span>
                  </div>
                  {agmtDetails.isFranchise && (
                    <div className="grid grid-cols-2 py-1">
                      <label className="expandedLabel fontExpand text-left ml-6">
                        <span className="">Franchise %: </span>
                      </label>
                      <span className="expandedData fontExpand ">
                        {agmtDetails.renewPercentageFranchise}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="commission-details py-4">
          <div className="text-base disclosureLabel">
            Split Rates Liability/Physical
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
              <label className="expandedLabel fontExpand text-left ml-2">
                <span className="">Split Rate: </span>
              </label>
              <span className="expandedData fontExpand ">
                {agmtDetails.isSplitRate === true ? "True" : "False"}
              </span>
            </div>
          </div>
          {agmtDetails.isSplitRate && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                <label className="expandedLabel fontExpand text-left ml-2">
                  <span className="">New Business Liability Rate: </span>
                </label>

                <span className="expandedData fontExpand ">
                  {newBusinessLiabilityRate > 0
                    ? newBusinessLiabilityRate.toFixed(2) + "%"
                    : newBusinessLiabilityRate}
                </span>
              </div>
              <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                <label className="expandedLabel fontExpand text-left ml-2">
                  <span className="">New Business Physical Damage Rate: </span>
                </label>

                <span className="expandedData fontExpand ">
                  {newBusinessPhysicalDamageRate > 0
                    ? newBusinessPhysicalDamageRate.toFixed(2) + "%"
                    : newBusinessPhysicalDamageRate}
                </span>
              </div>
              <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                <label className="expandedLabel fontExpand text-left ml-2">
                  <span className="">Renewed Liability Rate: </span>
                </label>

                <span className="expandedData fontExpand ">
                  {renewalLiabilityRate > 0
                    ? renewalLiabilityRate.toFixed(2) + "%"
                    : renewalLiabilityRate}
                </span>
              </div>
              <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                <label className="expandedLabel fontExpand text-left ml-2">
                  <span className="">Renewed Physical Damage Rate: </span>
                </label>

                <span className="expandedData fontExpand ">
                  {renewalPhysicalDamageRate > 0
                    ? renewalPhysicalDamageRate.toFixed(2) + "%"
                    : renewalPhysicalDamageRate}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="commission-details py-4">
          <div className="text-base disclosureLabel">Sliding Scales</div>
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
              <label className="expandedLabel fontExpand text-left ml-2">
                <span className="">Sliding Scales: </span>
              </label>
              <span className="expandedData fontExpand ">
                {agmtDetails.isSlidingScale === true ? "True" : "False"}
              </span>
            </div>
          </div>
          {agmtDetails.isSlidingScale && (
            <>
              {slidingScales.map((scale) => (
                <div className="grid grid-cols-1 md:grid-cols-3">
                  <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                    <label className="expandedLabel fontExpand text-left ml-2">
                      <span className="">Premium Range Start: </span>
                    </label>

                    <span className="expandedData fontExpand ">
                      {scale.premiumRangeStart ?? 0 > 0
                        ? scale.premiumRangeStart?.toFixed(2)
                        : scale.premiumRangeStart}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                    <label className="expandedLabel fontExpand text-left ml-2">
                      <span className="">Premium Range End: </span>
                    </label>

                    <span className="expandedData fontExpand ">
                      {scale.premiumRangeEnd ?? 0 > 0
                        ? scale.premiumRangeEnd?.toFixed(2)
                        : scale.premiumRangeEnd}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 w-full border-b border-gray-200 py-1">
                    <label className="expandedLabel fontExpand text-left ml-2">
                      <span className="">Percentage Rate: </span>
                    </label>

                    <span className="expandedData fontExpand ">
                      {scale.percentageRate ?? 0 > 0
                        ? scale.percentageRate?.toFixed(2) + "%"
                        : scale.percentageRate}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      {entityShow && (
        <EntityModal
          entityData={entityDetails}
          isOpen={entityShow}
          onClose={() => setEntityShow(false)}
        />
      )}
      {agencyShow && (
        <AgencyModal
          agencyData={agencyDetails}
          selectedAgency={agmtDetails.agency}
          isOpen={agencyShow}
          onClose={() => setAgencyShow(false)}
        />
      )}
      {producerCodeShow && (
        <ProducerModal
          isOpen={producerCodeShow}
          onClose={() => setProducerCodeShow(false)}
        />
      )}
      {policyPrefixShow && (
        <PolicyPrefixModal
          isOpen={policyPrefixShow}
          onClose={() => setPolicyPrefixShow(false)}
        />
      )}
    </>
  );
};

export default AgmtDetails;
